<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watchEffect } from "vue";
import { Download, Pause, Play, Volume2 } from "lucide-vue-next";
import { api } from "../services/api";
import type { AgentSummary } from "../types";

const props = defineProps<{ agents: AgentSummary[] }>();
const selectedAgent = ref("");
const text = ref("欢迎使用语音合成能力验证台。");
const loading = ref(false);
const error = ref("");
const audioUrl = ref("");
const audio = ref<HTMLAudioElement>();
const playing = ref(false);
const ttsAgents = computed(() => props.agents.filter((agent) => agent.capabilities.tts));
const activeAgent = computed(() => selectedAgent.value || ttsAgents.value[0]?.slug || "");

watchEffect(() => {
  if (!selectedAgent.value && ttsAgents.value[0]) selectedAgent.value = ttsAgents.value[0].slug;
});

async function generate(): Promise<void> {
  if (!activeAgent.value || !text.value.trim()) return;
  loading.value = true;
  error.value = "";
  playing.value = false;
  audio.value?.pause();
  if (audioUrl.value) URL.revokeObjectURL(audioUrl.value);
  try {
    const blob = await api.tts(activeAgent.value, text.value.trim());
    audioUrl.value = URL.createObjectURL(blob);
    audio.value = new Audio(audioUrl.value);
    audio.value.onended = () => { playing.value = false; };
    await audio.value.play();
    playing.value = true;
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : "语音生成失败";
  } finally {
    loading.value = false;
  }
}

async function togglePlayback(): Promise<void> {
  if (!audio.value) return;
  if (playing.value) audio.value.pause();
  else await audio.value.play();
  playing.value = !playing.value;
}

onBeforeUnmount(() => {
  audio.value?.pause();
  if (audioUrl.value) URL.revokeObjectURL(audioUrl.value);
});
</script>

<template>
  <div class="split-layout tts-layout">
    <section class="surface-section tts-editor">
      <div class="section-header"><div><span class="eyebrow">输入</span><h2>合成文本</h2></div></div>
      <label class="form-field">
        <span>智能体</span>
        <select v-model="selectedAgent">
          <option value="" disabled>{{ ttsAgents.length ? "请选择" : "没有启用 TTS 的智能体" }}</option>
          <option v-for="agent in ttsAgents" :key="agent.slug" :value="agent.slug">{{ agent.name }}</option>
        </select>
      </label>
      <label class="form-field grow">
        <span>文本</span>
        <textarea v-model="text" maxlength="5000" />
        <small>{{ text.length }} / 5000</small>
      </label>
      <button class="button primary full" type="button" :disabled="loading || !activeAgent || !text.trim()" @click="generate">
        <Volume2 :size="17" />{{ loading ? "生成中" : "生成并播放" }}
      </button>
      <p v-if="error" class="form-error">{{ error }}</p>
    </section>

    <section class="surface-section audio-result">
      <div class="wave-display" :class="{ active: playing }">
        <span v-for="index in 36" :key="index" :style="{ height: `${18 + ((index * 17) % 54)}%` }" />
      </div>
      <div class="audio-result-copy">
        <strong>{{ audioUrl ? "音频已生成" : "等待合成" }}</strong>
        <span>{{ ttsAgents.find((agent) => agent.slug === activeAgent)?.name ?? "未选择智能体" }}</span>
      </div>
      <div class="audio-actions">
        <button class="round-action" type="button" title="播放或暂停" :disabled="!audioUrl" @click="togglePlayback">
          <Pause v-if="playing" :size="21" /><Play v-else :size="21" />
        </button>
        <a :href="audioUrl || undefined" download="speech.mp3" :class="['icon-button', { disabled: !audioUrl }]" title="下载"><Download :size="18" /></a>
      </div>
    </section>
  </div>
</template>
